/**
 * Console accounts and what each may open.
 *
 * The identity service signs every account's permissions into its token, and every
 * API route checks them. `can()` here only decides what to render; it is never the
 * access-control boundary.
 *
 * Two roles. An administrator holds every permission, managing employees included.
 * An employee holds only the pages an administrator gave them. Each page has a view
 * permission and, where there is anything to change, an edit permission, which
 * includes viewing.
 */

export type Role = 'admin' | 'employee';

export const ROLE_LABELS: Record<Role, string> = {
  admin: 'Administrator',
  employee: 'Employee',
};

export const ROLE_DESCRIPTIONS: Record<Role, string> = {
  admin: 'Every page, including Employees: can create logins and change anyone’s access.',
  employee: 'Only the pages you choose below, each as view only or view and edit.',
};

export type Capability =
  | 'dashboard.view'
  | 'bookings.view'
  | 'bookings.edit'
  | 'catalogue.view'
  | 'catalogue.edit'
  | 'categories.view'
  | 'categories.edit'
  | 'production.view'
  | 'production.edit'
  | 'therapists.view'
  | 'therapists.edit'
  | 'applications.view'
  | 'applications.decide'
  | 'payouts.view'
  | 'payouts.edit'
  | 'customers.view'
  | 'customers.edit'
  | 'offers.view'
  | 'offers.edit'
  | 'reviews.view'
  | 'reviews.moderate'
  | 'notifications.view'
  | 'notifications.send'
  | 'banners.view'
  | 'banners.edit'
  | 'locations.view'
  | 'locations.edit'
  | 'settings.view'
  | 'settings.edit'
  | 'staff.manage';

export interface StaffUser {
  id: string;
  name: string;
  email: string;
  role: Role;
  /** Which city this account is scoped to. `null` means all cities. */
  city: string | null;
  lastActiveAt: string;
  active: boolean;
  /** What the account may do now: everything for an administrator. */
  capabilities?: Capability[];
  phone?: string;
  jobTitle?: string;
  employeeCode?: string;
  notes?: string;
  /** True until the person replaces the password they were given. */
  mustChangePassword?: boolean;
  createdAt?: string;
}

/** One console page, as the access picker and the navigation describe it. */
export interface ConsolePage {
  key: string;
  label: string;
  path: string;
  view: Capability;
  edit?: Capability;
  /** What viewing shows, and what editing adds, for the access picker. */
  viewHint: string;
  editHint?: string;
}

/** The pages an employee can be given, in the navigation's order. */
export const CONSOLE_PAGES: ConsolePage[] = [
  {
    key: 'dashboard',
    label: 'Dashboard',
    path: '/',
    view: 'dashboard.view',
    viewHint: 'Today’s schedule with addresses, the month’s numbers and what needs attention.',
  },
  {
    key: 'bookings',
    label: 'Bookings',
    path: '/bookings',
    view: 'bookings.view',
    edit: 'bookings.edit',
    viewHint: 'Every booking with the customer’s address, payments and refunds.',
    editHint: 'Change status, reassign, reschedule, cancel and retry refunds.',
  },
  {
    key: 'therapists',
    label: 'Therapists',
    path: '/therapists',
    view: 'therapists.view',
    edit: 'therapists.edit',
    viewHint: 'The roster, ratings and each therapist’s kit bag.',
    editHint: 'Edit profiles, take therapists off duty, issue and take back kits.',
  },
  {
    key: 'applications',
    label: 'Applications',
    path: '/applications',
    view: 'applications.view',
    edit: 'applications.decide',
    viewHint: 'Partner applications and their documents.',
    editHint: 'Review documents, interview, approve, reject and see bank details.',
  },
  {
    key: 'payouts',
    label: 'Payouts',
    path: '/payouts',
    view: 'payouts.view',
    edit: 'payouts.edit',
    viewHint: 'Withdrawals and what each partner is owed.',
    editHint: 'Pay withdrawals, see full account numbers and add ledger entries.',
  },
  {
    key: 'customers',
    label: 'Customers',
    path: '/customers',
    view: 'customers.view',
    edit: 'customers.edit',
    viewHint: 'Customer accounts, contact details and booking history.',
    editHint: 'Block accounts, set membership and write notes.',
  },
  {
    key: 'production',
    label: 'Production',
    path: '/production',
    view: 'production.view',
    edit: 'production.edit',
    viewHint: 'Kit stock, refill requests and the warehouse.',
    editHint: 'Issue and receive stock, decide refills and edit kit types.',
  },
  {
    key: 'packages',
    label: 'Packages',
    path: '/packages',
    view: 'catalogue.view',
    edit: 'catalogue.edit',
    viewHint: 'Every package, its types, add-ons and prices.',
    editHint: 'Create, edit, publish and archive packages.',
  },
  {
    key: 'categories',
    label: 'Categories',
    path: '/categories',
    view: 'categories.view',
    edit: 'categories.edit',
    viewHint: 'The service categories.',
    editHint: 'Create, edit and delete categories.',
  },
  {
    key: 'offers',
    label: 'Offers & coupons',
    path: '/offers',
    view: 'offers.view',
    edit: 'offers.edit',
    viewHint: 'Coupons and how often they were used.',
    editHint: 'Create, pause and delete coupons.',
  },
  {
    key: 'reviews',
    label: 'Reviews',
    path: '/reviews',
    view: 'reviews.view',
    edit: 'reviews.moderate',
    viewHint: 'Customer reviews, published or not.',
    editHint: 'Publish and hide reviews.',
  },
  {
    key: 'notifications',
    label: 'Notifications',
    path: '/notifications',
    view: 'notifications.view',
    edit: 'notifications.send',
    viewHint: 'What was sent to customers and partners.',
    editHint: 'Send announcements to every customer, by push as well.',
  },
  {
    key: 'banners',
    label: 'Banners',
    path: '/banners',
    view: 'banners.view',
    edit: 'banners.edit',
    viewHint: 'The website hero and the home carousel slides.',
    editHint: 'Change the hero’s words and the slides, and upload photos.',
  },
  {
    key: 'locations',
    label: 'Locations',
    path: '/locations',
    view: 'locations.view',
    edit: 'locations.edit',
    viewHint: 'Which cities are open, and their radius.',
    editHint: 'Open and close cities and states.',
  },
  {
    key: 'settings',
    label: 'Settings',
    path: '/settings',
    view: 'settings.view',
    edit: 'settings.edit',
    viewHint: 'Business details, booking rules, partner earnings and integrations.',
    editHint: 'Change them, and pause or resume bookings.',
  },
];

export const ALL_CAPABILITIES: Capability[] = [
  ...CONSOLE_PAGES.flatMap((page) => (page.edit ? [page.view, page.edit] : [page.view])),
  'staff.manage',
];

/**
 * Data several pages show, and the pages that may read it. Mirrors the shared reads
 * in the backend's authx package; a page whose data is not readable leaves that part
 * out rather than showing zeros.
 */
const READS = {
  bookings: ['bookings.view', 'dashboard.view', 'customers.view'],
  professionals: [
    'therapists.view',
    'bookings.view',
    'dashboard.view',
    'production.view',
    'reviews.view',
    'payouts.view',
    'locations.view',
  ],
  reviews: ['reviews.view', 'dashboard.view', 'therapists.view'],
  coupons: ['offers.view', 'dashboard.view'],
  customers: ['customers.view'],
  notifications: ['notifications.view'],
  applications: ['applications.view'],
  staff: ['staff.manage'],
  audit: ['staff.manage'],
} satisfies Record<string, Capability[]>;

export type SharedData = keyof typeof READS;

/** Everything an account may do. */
export function capabilitiesOf(user: StaffUser | undefined): Capability[] {
  if (!user) return [];
  if (user.role === 'admin') return ALL_CAPABILITIES;
  return user.capabilities ?? [];
}

/**
 * Presentation-level permission check: hides what an account cannot use. Never rely
 * on it to protect data; the API refuses the same things.
 */
export function can(user: StaffUser | undefined, capability: Capability): boolean {
  if (!user) return false;
  if (user.role === 'admin') return true;
  // An employee never manages staff, whatever arrives from the server.
  if (capability === 'staff.manage') return false;
  return (user.capabilities ?? []).includes(capability);
}

export function canAny(user: StaffUser | undefined, capabilities: readonly Capability[]): boolean {
  return capabilities.some((capability) => can(user, capability));
}

/** Whether the account may read data several pages share. */
export function canRead(user: StaffUser | undefined, data: SharedData): boolean {
  return canAny(user, READS[data]);
}

/** Where an account lands after signing in: the first page it may open. */
export function landingPath(user: StaffUser | undefined): string | null {
  return CONSOLE_PAGES.find((page) => can(user, page.view))?.path ?? null;
}

/** How much of one page an employee has: nothing, view, or view and edit. */
export type PageAccess = 'none' | 'view' | 'edit';

export function pageAccess(capabilities: readonly Capability[], page: ConsolePage): PageAccess {
  if (page.edit && capabilities.includes(page.edit)) return 'edit';
  return capabilities.includes(page.view) ? 'view' : 'none';
}

/** Applies one page's choice to a permission list, keeping console order. */
export function withPageAccess(
  capabilities: readonly Capability[],
  page: ConsolePage,
  access: PageAccess,
): Capability[] {
  const next = new Set(capabilities.filter((c) => c !== page.view && c !== page.edit));
  if (access !== 'none') next.add(page.view);
  if (access === 'edit' && page.edit) next.add(page.edit);
  return ALL_CAPABILITIES.filter((c) => next.has(c));
}
